import type { Node } from 'reactflow';

/**
 * Return a copy of `nodes` with:
 *   1. The deleted nodes removed.
 *   2. Every branch reference to any deleted node cleared (set to `undefined`).
 *
 * Covers the three branch shapes that packages/dsl/src/page-branches.ts
 * recognises:
 *   - MultiChoice  → `element.branches[].targetPageId`
 *   - YesNo        → `element.branches.yes` / `element.branches.no`
 *   - Button       → `element.targetPageId`  (any Button with a targetPageId)
 *
 * Used by both `deleteNode` (PropertyPanel delete button) and
 * `onNodesChangeWrapped` (keyboard Delete / backspace) so that all deletion
 * paths clear stale branch targets, regardless of how the deletion originated.
 */
export function detachReferencesTo(nodes: Node[], removedIds: string[]): Node[] {
  if (removedIds.length === 0) return nodes;

  const removedSet = new Set(removedIds);

  /** Clear every branch in a single element that points at a removed node. */
  function cleanElement(el: any): any {
    // MultiChoice: branches is an array of { option, targetPageId? }
    if (el.type === 'MultiChoice' && Array.isArray(el.branches)) {
      const cleaned = el.branches.map((b: any) =>
        removedSet.has(b.targetPageId) ? { ...b, targetPageId: undefined } : b,
      );
      // Only allocate a new element object if something actually changed.
      const changed = cleaned.some((b: any, i: number) => b !== el.branches[i]);
      return changed ? { ...el, branches: cleaned } : el;
    }

    // YesNo: branches is { yes?: string, no?: string }
    if (el.type === 'YesNo' && el.branches != null && typeof el.branches === 'object' && !Array.isArray(el.branches)) {
      const b = el.branches as { yes?: string; no?: string };
      const yesChanged = b.yes != null && removedSet.has(b.yes);
      const noChanged  = b.no  != null && removedSet.has(b.no);
      if (!yesChanged && !noChanged) return el;
      return {
        ...el,
        branches: {
          ...b,
          ...(yesChanged ? { yes: undefined } : {}),
          ...(noChanged  ? { no:  undefined } : {}),
        },
      };
    }

    // Button with a direct targetPageId
    if (
      el.type === 'Button' &&
      el.targetPageId != null &&
      removedSet.has(el.targetPageId)
    ) {
      return { ...el, targetPageId: undefined };
    }

    return el;
  }

  /** Rewrite a page node's elements, returning the same object if nothing changed. */
  function cleanNode(node: Node): Node {
    const elements: any[] = node.data?.elements;
    if (!Array.isArray(elements) || elements.length === 0) return node;

    const cleaned = elements.map(cleanElement);
    const changed = cleaned.some((el, i) => el !== elements[i]);
    if (!changed) return node;

    return { ...node, data: { ...node.data, elements: cleaned, _regenerateStubs: true } };
  }

  return nodes
    .filter((n) => !removedSet.has(n.id))
    .map(cleanNode);
}
